import { useEffect, useState, type CSSProperties } from 'react';

const KEYBOARD_MIN_PX = 150;
const SILENT_FALLBACK_MS = 350;
const SILENT_FALLBACK_MAX_PX = 320;

interface VisualViewportRect {
  offsetTop: number;
  height: number;
  keyboardOpen: boolean;
  /** Pixels the software keyboard covers. 0 when closed. */
  inset: number;
  /**
   * Keyboard covers the bottom but the WebView did not shrink visualViewport /
   * innerHeight. Typical of Instagram Android + cross-origin iframes (Payrails):
   * parent `pointerdown`/`focusin` never fire, and `adjustResize` does not run.
   */
  assumed: boolean;
}

interface VirtualKeyboard {
  boundingRect: DOMRect;
  addEventListener: (type: 'geometrychange', listener: () => void) => void;
  removeEventListener: (type: 'geometrychange', listener: () => void) => void;
}

function getVirtualKeyboard(): VirtualKeyboard | undefined {
  return (navigator as Navigator & { virtualKeyboard?: VirtualKeyboard }).virtualKeyboard;
}

function virtualKeyboardHeight(): number {
  return Math.round(getVirtualKeyboard()?.boundingRect.height ?? 0);
}

export function isTypedFocus(el: Element | null): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    tag === 'IFRAME' ||
    el.isContentEditable
  );
}

/** Parent-document fields that open the keyboard. An iframe with tabindex can
 *  be `document.activeElement` without a keyboard (Payrails host frame). */
function isNativeTypedFocus(el: Element | null): el is HTMLElement {
  return isTypedFocus(el) && el.tagName !== 'IFRAME';
}

let lastTypedFocusAt = 0;
/** True only after a pointer on a field — not after Continue focusing an input. */
let fieldPointerActive = false;
/**
 * Payrails (and similar) hosted fields: the parent never sees the tap/focus, so
 * `fieldPointerActive` and `document.activeElement` stay false. Set from
 * `requestKeepVisible` when the SDK reports FOCUS.
 */
let hostedFieldActive = false;
/** Last read had a real viewport shrink. Used to drop the silent lift on IME hide. */
let geometryWasOpen = false;

const viewportListeners = new Set<() => void>();

function notifyViewportListeners() {
  viewportListeners.forEach((listener) => listener());
}

function dropSilentKeyboardAssumption() {
  hostedFieldActive = false;
  fieldPointerActive = false;
  lastTypedFocusAt = 0;
}

/** Call when a hosted iframe (Payrails) takes focus — parent `focusin` may not fire. */
export function markTypedFocus(fromUserField = false) {
  lastTypedFocusAt = Date.now();
  if (fromUserField) {
    fieldPointerActive = true;
    hostedFieldActive = true;
  }
  notifyViewportListeners();
}

/** Drop the hosted-iframe keyboard assumption (sheet close / field blur / IME hide). */
export function clearHostedFieldFocus() {
  dropSilentKeyboardAssumption();
  notifyViewportListeners();
}

function isFieldPointerTarget(el: EventTarget | null): boolean {
  if (!(el instanceof Element)) return false;
  return Boolean(
    el.closest('input, textarea, select, iframe, [contenteditable], [data-keyboard-anchor]'),
  );
}

function likelyMobileKeyboard(): boolean {
  return window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 700;
}

/**
 * Largest viewport height seen while the keyboard is closed. On iOS the layout
 * viewport stays full and visualViewport shrinks (delta = keyboard). On Android
 * Chrome with `interactive-widget=resizes-content` BOTH shrink, so the delta is
 * ~0 — we compare against this resting height instead.
 *
 * Monotonic except on width change (rotate / split-screen). Shrinking it when
 * `innerHeight` drops first (Instagram) poisons detection forever.
 */
let restingHeight = 0;
let restingWidth = 0;

function readViewport(): VisualViewportRect {
  const viewport = window.visualViewport;
  const height = viewport?.height ?? window.innerHeight;
  const offsetTop = viewport?.offsetTop ?? 0;
  const layoutHeight = window.innerHeight;
  const layoutWidth = window.innerWidth;
  const visible = height + offsetTop;

  if (restingWidth !== 0 && Math.abs(layoutWidth - restingWidth) > 50) {
    restingHeight = Math.max(layoutHeight, visible);
    restingWidth = layoutWidth;
  } else {
    if (restingHeight === 0) restingWidth = layoutWidth;
    restingHeight = Math.max(restingHeight, layoutHeight, visible);
  }

  const overlayCovered = layoutHeight - height;
  const resizedCovered = restingHeight - height;
  const vkCovered = virtualKeyboardHeight();
  const covered = Math.max(overlayCovered, resizedCovered, vkCovered);
  const geometryOpen = covered > KEYBOARD_MIN_PX;

  if (geometryOpen) {
    geometryWasOpen = true;
    return {
      offsetTop,
      height,
      keyboardOpen: true,
      inset: Math.round(covered),
      assumed: false,
    };
  }

  // IME "OK"/Done: Android often leaves the field focused while the viewport
  // grows back. Without this, delayedSilent / hostedSilent would keep the
  // sheet lifted after the keyboard is gone.
  if (geometryWasOpen) {
    geometryWasOpen = false;
    dropSilentKeyboardAssumption();
    return { offsetTop, height, keyboardOpen: false, inset: 0, assumed: false };
  }

  // Hosted iframe: assume immediately — there is no parent focusin to start the
  // 350ms timer, and `document.activeElement` often stays on <body>.
  const hostedSilent = likelyMobileKeyboard() && hostedFieldActive;
  const delayedSilent =
    likelyMobileKeyboard() &&
    fieldPointerActive &&
    isNativeTypedFocus(document.activeElement) &&
    lastTypedFocusAt > 0 &&
    Date.now() - lastTypedFocusAt >= SILENT_FALLBACK_MS;

  if (hostedSilent || delayedSilent) {
    const assumed = Math.round(Math.min(layoutHeight * 0.4, SILENT_FALLBACK_MAX_PX));
    return {
      offsetTop: 0,
      height: Math.max(0, height - assumed),
      keyboardOpen: true,
      inset: assumed,
      assumed: true,
    };
  }

  return { offsetTop, height, keyboardOpen: false, inset: 0, assumed: false };
}

/** Current visible viewport, including the silent-webview keyboard fallback. */
export function getVisibleViewport(): VisualViewportRect {
  if (typeof window === 'undefined') {
    return { offsetTop: 0, height: 0, keyboardOpen: false, inset: 0, assumed: false };
  }
  return readViewport();
}

/** Tracks the visible viewport so sheets sit above the software keyboard. */
export function useVisualViewport(active = true): VisualViewportRect {
  const [rect, setRect] = useState<VisualViewportRect>(() =>
    typeof window === 'undefined'
      ? { offsetTop: 0, height: 0, keyboardOpen: false, inset: 0, assumed: false }
      : readViewport(),
  );

  useEffect(() => {
    if (!active) return;
    const viewport = window.visualViewport;
    const vk = getVirtualKeyboard();

    const update = () => setRect(readViewport());
    update();

    let fallbackTimer: ReturnType<typeof setTimeout> | undefined;
    const onPointerDown = (event: PointerEvent) => {
      fieldPointerActive = isFieldPointerTarget(event.target);
    };
    const onFocusIn = () => {
      const active = document.activeElement;
      if (!isTypedFocus(active)) return;
      markTypedFocus();
      update();
      clearTimeout(fallbackTimer);
      // Iframe focus ≠ keyboard. Payrails arms the fallback via requestKeepVisible.
      if (!fieldPointerActive || active.tagName === 'IFRAME') return;
      fallbackTimer = setTimeout(update, SILENT_FALLBACK_MS + 10);
    };
    const onFocusOut = () => {
      clearTimeout(fallbackTimer);
      fallbackTimer = setTimeout(() => {
        if (!isTypedFocus(document.activeElement)) dropSilentKeyboardAssumption();
        update();
      }, 80);
    };
    // Gboard / Samsung "OK": often Enter + `change`, field stays focused, no resize.
    const dismissIfImeDone = () => {
      if (!hostedFieldActive && !fieldPointerActive) return;
      dropSilentKeyboardAssumption();
      update();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter') return;
      const el = document.activeElement;
      if (el instanceof HTMLElement && isNativeTypedFocus(el)) el.blur();
      dismissIfImeDone();
    };
    const onNativeChange = (event: Event) => {
      const target = event.target;
      if (
        !(target instanceof HTMLInputElement) &&
        !(target instanceof HTMLTextAreaElement)
      ) {
        return;
      }
      // Blur-`change` (moving to another field) must not drop the lift.
      // IME "OK" commits `change` while the same input stays focused.
      if (document.activeElement !== target) return;
      target.blur();
      dismissIfImeDone();
    };

    viewportListeners.add(update);
    viewport?.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    vk?.addEventListener('geometrychange', update);
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('change', onNativeChange, true);
    return () => {
      clearTimeout(fallbackTimer);
      viewportListeners.delete(update);
      viewport?.removeEventListener('resize', update);
      viewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      vk?.removeEventListener('geometrychange', update);
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('change', onNativeChange, true);
    };
  }, [active]);

  return rect;
}

/**
 * Safari: `position:fixed; top:0` is layout-viewport-relative, so after a
 * visual-viewport pan the probe sits above the screen (negative rect.top).
 * Instagram: `fixed` is already visual-relative — the probe stays at 0, and
 * following `offsetTop` would double-shift the form and desync taps.
 */
function pinTop(offsetTop: number): number {
  if (offsetTop <= 20 || typeof document === 'undefined') return 0;
  const probe = document.createElement('div');
  probe.setAttribute('aria-hidden', 'true');
  probe.style.cssText =
    'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;pointer-events:none';
  document.body.appendChild(probe);
  const top = probe.getBoundingClientRect().top;
  probe.remove();
  return top < -10 ? offsetTop : 0;
}

/**
 * Pin a full-screen form to the visible visual viewport while the keyboard is
 * open. Do not use `transform` — Instagram's WKWebView leaves hit-targets shifted
 * after the keyboard closes.
 */
export function useKeyboardScreenStyle(): CSSProperties | undefined {
  const { height, offsetTop, keyboardOpen } = useVisualViewport();
  if (!keyboardOpen || !height) return undefined;
  return {
    position: 'fixed',
    top: pinTop(offsetTop),
    left: 0,
    right: 0,
    width: '100%',
    height,
    minHeight: height,
    maxHeight: height,
    // Above `.safari-chrome-tint` (z-index 5) so the keyboard-gap fill cannot
    // cover the focused field while the form is still in-flow.
    zIndex: 20,
  };
}
